import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';

import { Grade } from '../../models/grade.school.model';
import * as gradeActions from '../actions/grade.school.actions';

export interface GradeState extends EntityState<Grade> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  error: AppExceptionModel | undefined;
}

export const gradeAdapter: EntityAdapter<Grade> = createEntityAdapter<Grade>();

export const initialState: GradeState = gradeAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  error: undefined,
});

export const schoolGradeReducer = createReducer(
  initialState,
  on(gradeActions.getGrades, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(gradeActions.getGrade, (state, {}) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeActions.getGradeSuccess, (state, { grade }) =>
    gradeAdapter.setOne(grade, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeActions.getGradeFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeActions.addGradeToStore, (state, { grade }) =>
    gradeAdapter.upsertOne(grade, {
      ...state,
      error: undefined,
      loading: false,
      inProgress: false,
      loaded: true,
    }),
  ),
  on(gradeActions.addGradesToStore, (state, { grades }) =>
    gradeAdapter.upsertMany(grades, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      error: undefined,
    }),
  ),
  on(gradeActions.getGradesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error: error,
  })),
  on(gradeActions.create, (state) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeActions.createSuccess, (state, { grade }) =>
    gradeAdapter.upsertOne(grade, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeActions.update, (state) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeActions.updateSuccess, (state, { grade }) =>
    gradeAdapter.upsertOne(grade, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeActions.reassignGrade, (state) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeActions.reassignGradeFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeActions.reassignGradeSuccess, (state) => ({
    ...state,
    error: undefined,
    inProgress: false,
  })),
  on(gradeActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(gradeActions.deleteSuccess, (state, { id }) =>
    gradeAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeActions.clearFromStore, (state, { ids }) =>
    gradeAdapter.removeMany(ids, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(gradeActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeActions.clear, (state) => initialState),
);
